import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { useMediaQuery } from 'react-responsive';
import { Container, Row, Column, Button, List, Alert, Checkbox, StarRating, ReviewCard, Dropdown, Icon } from '@pella/sash-framework';

import styles from './index.module.scss';

const CustomerReviews = ({ data, title, className, totalReviews, overallRating }) => {
	const configData = useStaticQuery(query).contentstackGlobalConfig?.reviews;
	const [loading, setLoading] = useState(true);
	const [sortedReviews, setSortedReviews] = useState([]);
	const [reviewsToShow, setReviewsToShow] = useState(10);
	const [activeSort, setActiveSort] = useState({ title: 'Most Recent', sort_name: 'most_recent' });
	const [ratingSelections, setRatingSelections] = useState([1, 2, 3, 4, 5]);
	const isTablet = useMediaQuery({ query: '(min-width: 768px) and (max-width: 1279px)' });
	const isDesktop = useMediaQuery({ query: '(min-width: 1280px)' });

	useEffect(() => {
		if (!data?.Reviews?.[0]) {
			setLoading(false);
			return;
		}

		setSortedReviews(data.Reviews.sort((a, b) => new Date(b.DateSurveyed) - new Date(a.DateSurveyed)));
		setLoading(false);
	}, [data]);

	if (!data) return null;

	const onLoadMoreClick = () => {
		const length = data.Reviews.length - reviewsToShow;
		length >= 10 ? setReviewsToShow(reviewsToShow + 10) : setReviewsToShow(reviewsToShow + length);
	};

	const onRatingSortSelection = (event) => {
		let newRatingSelection = ratingSelections;
		const value = parseInt(event.target.value);

		if (ratingSelections.includes(value)) {
			newRatingSelection = ratingSelections.filter((rating) => rating !== value);
			setRatingSelections(newRatingSelection);
		} else {
			newRatingSelection = [...ratingSelections, value];
			setRatingSelections(newRatingSelection);
		}

		setSortedReviews(data.Reviews.filter((review) => newRatingSelection.includes(review.OverallSatisfactionRating)));
		onSortSelect(activeSort.sort_name);
	};

	const onSortSelect = (type) => {
		if (type === 'most_recent') {
			setActiveSort({ title: 'Most Recent', sort_name: type });
			setSortedReviews((current) => current.sort((a, b) => new Date(b.DateSurveyed) - new Date(a.DateSurveyed)));
		}
		if (type === 'rating_high') {
			setActiveSort({ title: 'Rating (high to low)', sort_name: type });
			setSortedReviews((current) => current.sort((a, b) => b.OverallSatisfactionRating - a.OverallSatisfactionRating));
		}
		if (type === 'rating_low') {
			setActiveSort({ title: 'Rating (low to high)', sort_name: type });
			setSortedReviews((current) => current.sort((a, b) => a.OverallSatisfactionRating - b.OverallSatisfactionRating));
		}
	};

	const renderRatingColumn = (review, index) => {
		const date = new Date(review.DateSurveyed);

		return (
			<ReviewCard
				rating={review.OverallSatisfactionRating}
				date={date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
				className='mb-4 md:mb-6'
				key={index}
			>
				{review.WhyChosePella && (
					<>
						<ReviewCard.Heading content={configData?.heading_1} />
						<ReviewCard.Content content={review.WhyChosePella} />
					</>
				)}
				{review.MemorableAspects && (
					<>
						<ReviewCard.Heading content={configData?.heading_2} />
						<ReviewCard.Content content={review.MemorableAspects} />
					</>
				)}
				<ReviewCard.Author name={review.CustomerFirstName + ' ' + review.CustomerLastName} />
			</ReviewCard>
		);
	};

	return (
		!loading && (
			<div
				id='ratings-reviews'
				className={`${styles.sectionWrapper} ${styles.ratingsReviews} ${className || ''}`}
				data-silktide='ignore'
			>
				<Container>
					<h2>
						{title ? title : 'Customer Ratings & Reviews'}
						<a href='/support/ratings-and-reviews/'>*</a>
					</h2>

					{(data.Empty || totalReviews === 0) && (
						<Alert
							type='error'
							className={`${styles.ratingsReviews_Alert} mb-4`}
							hideClose={true}
						>
							There are currently no reviews for this showroom.
						</Alert>
					)}

					{!data.Empty && totalReviews > 0 && (
						<>
							<Row
								align='center'
								largeGutters={true}
								className={styles.ratingsReviews_Row}
							>
								<Column
									xs={12}
									md={6}
									lg='auto'
									className={styles.ratingsReviews_ScoreColumn}
								>
									<Row
										align='center'
										className={styles.ratingsReviews_ScoreRow}
									>
										<Column xs='auto'>
											<p className={styles.ratingsReviews_Rating}>{overallRating}</p>
										</Column>

										<Column
											xs='auto'
											sm='fluid'
										>
											<StarRating
												totalReviews={totalReviews}
												rating={overallRating}
												hideTotal={true}
												hideRating={true}
											/>

											<p>Based on {totalReviews} Ratings</p>
										</Column>
									</Row>
								</Column>

								<Column
									xs={12}
									sm={10}
									md={6}
									xl={4}
									xxl={3}
								>
									<List
										bullets='none'
										className={styles.ratingsReviews_RatingsBreakdown}
									>
										<li>
											<span>5 Stars</span>
											<span className={styles.ratingsReviews_BarBackground}>
												<span
													className={styles.ratingsReviews_Bar}
													style={{
														width: `calc((${data.Total5Star} / ${totalReviews}) * 100%)`,
													}}
												/>
											</span>
											<span>{data.Total5Star}</span>
										</li>
										<li>
											<span>4 Stars</span>
											<span className={styles.ratingsReviews_BarBackground}>
												<span
													className={styles.ratingsReviews_Bar}
													style={{
														width: `calc((${data.Total4Star} / ${totalReviews}) * 100%)`,
													}}
												/>
											</span>
											<span>{data.Total4Star}</span>
										</li>
										<li>
											<span>3 Stars</span>
											<span className={styles.ratingsReviews_BarBackground}>
												<span
													className={styles.ratingsReviews_Bar}
													style={{
														width: `calc((${data.Total3Star} / ${totalReviews}) * 100%)`,
													}}
												/>
											</span>
											<span>{data.Total3Star}</span>
										</li>
										<li>
											<span>2 Stars</span>
											<span className={styles.ratingsReviews_BarBackground}>
												<span
													className={styles.ratingsReviews_Bar}
													style={{
														width: `calc((${data.Total2Star} / ${totalReviews}) * 100%)`,
													}}
												/>
											</span>
											<span>{data.Total2Star}</span>
										</li>
										<li>
											<span>1 Star</span>
											<span className={styles.ratingsReviews_BarBackground}>
												<span
													className={styles.ratingsReviews_Bar}
													style={{
														width: `calc((${data.Total1Star} / ${totalReviews}) * 100%)`,
													}}
												/>
											</span>
											<span>{data.Total1Star}</span>
										</li>
									</List>
								</Column>
							</Row>

							<Row
								className='relative z-10 mb-5 md:justify-end'
								align='center'
							>
								<Column xs='auto'>
									<Dropdown className='customerReviews-ratingMenu'>
										<Dropdown.Trigger>
											Rating <Icon name='keyboard_arrow_down' />
										</Dropdown.Trigger>
										<Dropdown.Content className='mt-2 w-max p-4'>
											<List
												bullets='none'
												className='customerReviews-ratingList'
											>
												{[5, 4, 3, 2, 1].map((rate, index) => (
													<li key={index}>
														<Checkbox
															id={`rating_${rate}`}
															labelText={
																<StarRating
																	className='[&>p]:text-lg [&_span]:text-xl'
																	rating={rate}
																	hideRating
																/>
															}
															value={rate}
															onChange={onRatingSortSelection}
															defaultChecked={true}
															groupClass={index < 4 ? '[&_label]:mb-2' : '[&_label]:mb-0'}
														/>
													</li>
												))}
											</List>
										</Dropdown.Content>
									</Dropdown>
								</Column>

								<Column xs='auto'>
									<Dropdown className='customerReviews-sortMenu'>
										<Dropdown.Trigger>
											Sort By: <span className='ml-2 font-semibold'>{activeSort.title}</span>
											<Icon name='keyboard_arrow_down' />
										</Dropdown.Trigger>
										<Dropdown.Content className='mt-2 p-4'>
											<List
												bullets='none'
												className='customerReviews-sortList space-y-1'
											>
												<li>
													<button
														onClick={(event) => {
															event.preventDefault();
															onSortSelect('most_recent');
														}}
													>
														Most Recent
													</button>
												</li>
												<li>
													<button
														onClick={(event) => {
															event.preventDefault();
															onSortSelect('rating_high');
														}}
													>
														Rating (high to low)
													</button>
												</li>
												<li>
													<button
														onClick={(event) => {
															event.preventDefault();
															onSortSelect('rating_low');
														}}
													>
														Rating (low to high)
													</button>
												</li>
											</List>
										</Dropdown.Content>
									</Dropdown>
								</Column>
							</Row>

							{!loading && sortedReviews?.[0] && (
								<div className='customerReviews-reviewsRow relative'>
									<Row align='start'>
										<Column
											xs={12}
											md={6}
											xl={4}
										>
											{sortedReviews.slice(0, reviewsToShow).map((review, index) => {
												if (isDesktop && index % 3 > 0) return null;
												if (isTablet && index % 2 > 0) return null;

												return renderRatingColumn(review, index);
											})}
										</Column>

										{(isTablet || isDesktop) && (
											<Column
												xs={12}
												md={6}
												xl={4}
											>
												{sortedReviews.slice(0, reviewsToShow).map((review, index) => {
													if (isDesktop && index % 3 !== 1) return null;
													if (isTablet && index % 2 !== 1) return null;

													return renderRatingColumn(review, index);
												})}
											</Column>
										)}

										{isDesktop && (
											<Column
												xs={12}
												md={6}
												xl={4}
											>
												{sortedReviews.slice(0, reviewsToShow).map((review, index) => {
													if (index % 3 < 2) return null;

													return renderRatingColumn(review, index);
												})}
											</Column>
										)}
									</Row>

									{sortedReviews.length > reviewsToShow && (
										<div className='customerReviews-loadMore absolute -bottom-2.5 left-0 z-2 w-full bg-gradient-to-t from-white pb-0 pt-20 text-center md:pb-20'>
											<Button
												color='primary'
												onClick={onLoadMoreClick}
											>
												Show More Reviews
											</Button>
										</div>
									)}
								</div>
							)}
						</>
					)}
				</Container>
			</div>
		)
	);
};

CustomerReviews.propTypes = {
	data: PropTypes.object,
	totalReviews: PropTypes.number,
	overallRating: PropTypes.number,
	className: PropTypes.string,
	title: PropTypes.string,
};

export default CustomerReviews;

export const query = graphql`
	{
		contentstackGlobalConfig {
			reviews {
				heading
				description
				heading_1
				heading_2
			}
		}
	}
`;
